import React, { useState, useEffect, useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGlobe,
    faLock,
    faSave,
    faSpinner,
    faShieldAlt,
    faUserTie,
    faPen,
    faPlus,
    faFolderPlus,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { validateInappropriateLanguage } from '../../../utils/profanitySanitizer';

const AVAILABLE_PREFIX_VARIABLES = [
    { token: '{{nombre_agente}}', label: 'Nombre Asesor', icon: faUserTie }
];

const GlobalMessagesSettingsSection = () => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const { token, nombreCompleto, idCatTipoUsuario } = useToken();
    const isAdmin = Number(idCatTipoUsuario) === 1;
    const { showSuccessToast, showErrorToast } = useToast();

    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);
    const [prefixMap, setPrefixMap] = useState({});
    const [editingKey, setEditingKey] = useState(null);
    const [backupPrefix, setBackupPrefix] = useState('');

    // Modal de Nueva Categoría
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newCategoryKey, setNewCategoryKey] = useState('');
    const [newCategoryPrefix, setNewCategoryPrefix] = useState('');
    const [newCategoryDefaultMessage, setNewCategoryDefaultMessage] = useState('');
    const [isCreatingCategory, setIsCreatingCategory] = useState(false);

    // Cargar categorías y prefijos desde el backend
    const fetchGlobalCategories = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/SupportEngine/GetMensajesAutomaticos', {
                headers: {
                    'Accept': 'application/json',
                    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
                }
            });

            if (response.ok) {
                const result = await response.json();
                const list = result.data || result || [];
                if (Array.isArray(list)) {
                    // Nos aseguramos de incluir BIENVENIDA
                    let activeList = list;
                    if (!activeList.some(c => c.vClaveMensaje === 'BIENVENIDA')) {
                        activeList = [
                            {
                                idCatMensajesAutomaticos: 3,
                                vClaveMensaje: 'BIENVENIDA',
                                vPrefijo: 'Hola. Gracias por comunicarte con nuestro equipo, en este momento te está atendiendo {{nombre_agente}}'
                            },
                            ...activeList
                        ];
                    }
                    setCategories(activeList);

                    const map = {};
                    activeList.forEach(c => {
                        map[c.vClaveMensaje] = c.vPrefijo || (c.vClaveMensaje === 'BIENVENIDA' ? 'Hola. Gracias por comunicarte con nuestro equipo, en este momento te está atendiendo {{nombre_agente}}' : '');
                    });
                    setPrefixMap(map);
                }
            } else {
                if (showErrorToast) showErrorToast('Error al cargar la configuración de mensajes globales.');
            }
        } catch (err) {
            console.error('Error al cargar mensajes globales:', err);
            if (showErrorToast) showErrorToast('Error de conexión al cargar mensajes globales.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAdmin) return;
        fetchGlobalCategories();
    }, [token, isAdmin]);

    if (!isAdmin) {
        return (
            <div className="alert alert-warning p-4 rounded-3 text-center my-4">
                <FontAwesomeIcon icon={faShieldAlt} className="fa-2x mb-2 text-warning" />
                <h6 className="fw-bold mb-1">Acceso Restringido</h6>
                <p className="mb-0 small text-muted">
                    Esta sección de configuración institucional es exclusiva para administradores del sistema.
                </p>
            </div>
        );
    }

    const handlePrefixChange = (clave, text) => {
        setPrefixMap(prev => ({
            ...prev,
            [clave]: text
        }));
    };

    const handleInsertVariable = (clave, tokenStr) => {
        setPrefixMap(prev => {
            const current = prev[clave] || '';
            return {
                ...prev,
                [clave]: current ? `${current} ${tokenStr}` : tokenStr
            };
        });
    };

    const handleSavePrefix = async (clave) => {
        const prefixToSave = prefixMap[clave] || '';
        const validation = validateInappropriateLanguage(prefixToSave);
        if (!validation.isValid) {
            if (showErrorToast) {
                showErrorToast(`No se puede guardar: El prefijo contiene una palabra no permitida ("${validation.foundWord}").`);
            }
            return;
        }

        setSavingKey(clave);
        try {
            const payload = {
                messageKey: clave,
                prefix: prefixToSave,
                updatedUser: nombreCompleto || 'Administrador'
            };

            const response = await fetch('/api/SupportEngine/SaveCategoryPrefix', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                if (showSuccessToast) {
                    showSuccessToast(`Prefijo institucional para "${clave}" guardado correctamente.`);
                }
                setEditingKey(null);
            } else {
                const errData = await response.json().catch(() => ({}));
                if (showErrorToast) {
                    showErrorToast(errData.message || 'No se pudo guardar el prefijo.');
                }
            }
        } catch (err) {
            console.error('Error al guardar prefijo global:', err);
            if (showErrorToast) showErrorToast('Error al conectar con el servidor.');
        } finally {
            setSavingKey(null);
        }
    };

    const handleCreateCategory = async () => {
        const key = newCategoryKey.trim().toUpperCase().replace(/\s+/g, '_');
        if (!key) {
            if (showErrorToast) showErrorToast('Debes ingresar la clave de la nueva categoría.');
            return;
        }
        if (!newCategoryPrefix.trim()) {
            if (showErrorToast) showErrorToast('Debes ingresar el prefijo institucional para la categoría.');
            return;
        }

        const prefixVal = validateInappropriateLanguage(newCategoryPrefix);
        if (!prefixVal.isValid) {
            if (showErrorToast) showErrorToast(`El prefijo contiene una palabra no permitida ("${prefixVal.foundWord}").`);
            return;
        }

        if (newCategoryDefaultMessage.trim()) {
            const msgVal = validateInappropriateLanguage(newCategoryDefaultMessage);
            if (!msgVal.isValid) {
                if (showErrorToast) showErrorToast(`El mensaje adicional contiene una palabra no permitida ("${msgVal.foundWord}").`);
                return;
            }
        }

        setIsCreatingCategory(true);
        try {
            const payload = {
                idMensaje: 0,
                vClaveMensaje: key,
                vJsonMensajes: newCategoryDefaultMessage.trim() ? JSON.stringify([{
                    idUsuario: null,
                    vMensajePersonalizado: newCategoryDefaultMessage.trim()
                }]) : JSON.stringify([]),
                vCreatedUser: nombreCompleto || 'Administrador',
                vUpdatedUser: nombreCompleto || 'Administrador'
            };

            const response = await fetch('/api/SupportEngine/CreateMensajeAutomatico', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                if (showErrorToast) showErrorToast(errData.message || 'Error al crear la categoría.');
                return;
            }

            await fetch('/api/SupportEngine/SaveCategoryPrefix', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
                },
                body: JSON.stringify({
                    messageKey: key,
                    prefix: newCategoryPrefix.trim(),
                    updatedUser: nombreCompleto || 'Administrador'
                })
            });

            if (showSuccessToast) showSuccessToast(`Categoría "${key}" creada exitosamente.`);
            setIsCreateModalOpen(false);
            setNewCategoryKey('');
            setNewCategoryPrefix('');
            setNewCategoryDefaultMessage('');
            fetchGlobalCategories();
        } catch (err) {
            console.error('Error al crear categoría:', err);
            if (showErrorToast) showErrorToast('Error de conexión al crear categoría.');
        } finally {
            setIsCreatingCategory(false);
        }
    };

    return (
        <div className="container-fluid p-0">
            {/* Header Section */}
                <div
                    className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4 pb-3 border-bottom"
                    style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
                >
                    <div>
                        <h5
                            className="fw-bold mb-1 d-flex align-items-center gap-2"
                            style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}
                        >
                            <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                            <span>Mensajes Globales y Prefijos del Sistema</span>
                        </h5>
                        <p className="text-muted small mb-0" style={{ fontSize: '0.85rem' }}>
                            Define las categorías institucionales y los prefijos fijos obligatorios que se antepondrán a los mensajes de los asesores.
                        </p>
                    </div>

                    {/* Botón Nueva Categoría Exclusivo para Administrador */}

                </div>


                {/* Listado de Claves Configurables */}
                {isLoading ? (
                    <div className="text-center py-5">
                        <FontAwesomeIcon icon={faSpinner} spin className="text-primary fa-2x mb-3" />
                        <p className="text-muted small">Cargando configuración de prefijos globales...</p>
                    </div>
                ) : (
                    <div className="row g-4">
                        {categories.filter(cat => cat.vClaveMensaje !== 'PRIMER_MENSAJE').map(cat => {
                            const clave = cat.vClaveMensaje;
                            const currentPrefix = prefixMap[clave] ?? '';
                            const isSavingThis = savingKey === clave;
                            const isEditing = editingKey === clave;

                            return (
                                <div key={clave} className="col-12">
                                <div
                                    className="card border shadow-sm rounded-3"
                                    style={{
                                        backgroundColor: isDark ? '#121e2d' : '#ffffff',
                                        borderColor: isDark ? '#233142' : '#e2e8f0'
                                    }}
                                >
                                    <div
                                        className="card-header py-3 px-4 d-flex justify-content-between align-items-center border-bottom"
                                        style={{
                                            backgroundColor: isDark ? '#162232' : '#f8fafc',
                                            borderColor: isDark ? '#233142' : '#e2e8f0'
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge bg-primary rounded-pill px-3 py-1.5 fw-semibold font-monospace" style={{ fontSize: '0.82rem' }}>
                                                {clave}
                                            </span>
                                            <span className="fw-semibold small" style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}>
                                                {clave === 'BIENVENIDA' ? 'Mensaje de Asignación / Saludo Inicial' : 'Categoría Institucional'}
                                            </span>
                                        </div>
                                        <span className="badge bg-success bg-opacity-10 text-success border border-success px-2 py-1 small">
                                            Activo
                                        </span>
                                    </div>

                                    <div className="card-body p-4">
                                        {!isEditing ? (
                                            <div>
                                                <p className="text-muted small mb-3" style={{ fontSize: '0.82rem' }}>
                                                    {clave === 'BIENVENIDA'
                                                        ? 'Este texto se insertará automáticamente al inicio de cada mensaje de bienvenida enviado por los asesores al tomar un ticket.'
                                                        : `Prefijo institucional obligatorio que se antepondrá a los mensajes de la categoría "${clave}".`}
                                                </p>

                                                {/* Vista Previa del Prefijo */}
                                                <div
                                                    className="p-3 rounded-3 mb-4 border"
                                                    style={{
                                                        backgroundColor: isDark ? '#0d1520' : '#f8fafc',
                                                        borderColor: isDark ? '#1e2d3d' : '#e2e8f0'
                                                    }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <small className="text-muted fw-semibold" style={{ fontSize: '0.74rem' }}>
                                                            VISTA PREVIA DEL PREFIJO INSTITUCIONAL:
                                                        </small>
                                                        <span
                                                            className="badge d-inline-flex align-items-center gap-1"
                                                            style={{
                                                                backgroundColor: 'transparent',
                                                                color: isDark ? '#93c5fd' : '#475569',
                                                                fontSize: '0.74rem'
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faShieldAlt} /> Fijo en plantilla
                                                        </span>
                                                    </div>
                                                    <div
                                                        className="p-2.5 rounded-2 d-flex justify-content-between align-items-center flex-wrap gap-2 border"
                                                        style={{
                                                            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eff6ff',
                                                            borderColor: isDark ? 'rgba(59, 130, 246, 0.25)' : '#bfdbfe',
                                                            color: isDark ? '#93c5fd' : '#1e40af',
                                                            fontSize: '0.85rem',
                                                            lineHeight: '1.6'
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center gap-2">
                                                            <FontAwesomeIcon icon={faLock} />
                                                            <span>
                                                                <strong>[Prefijo]:</strong>{' '}
                                                                {currentPrefix || <span className="opacity-50 fst-italic">Sin prefijo configurado</span>}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Botón Editar */}
                                                <div className="d-flex justify-content-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary rounded-pill px-4 shadow-none d-flex align-items-center gap-2"
                                                        onClick={() => {
                                                            setEditingKey(clave);
                                                            setBackupPrefix(currentPrefix);
                                                        }}
                                                        style={{ fontSize: '0.86rem' }}
                                                    >
                                                        <FontAwesomeIcon icon={faPen} />
                                                        <span>Editar Prefijo</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <label
                                                    className="form-label small fw-semibold mb-1 d-flex justify-content-between align-items-center"
                                                    style={{ color: isDark ? '#cbd5e1' : '#475569' }}
                                                >
                                                    <span>Edición de Prefijo</span>
                                                    <span className="text-muted font-monospace" style={{ fontSize: '0.74rem' }}>
                                                        {currentPrefix.length} caracteres
                                                    </span>
                                                </label>

                                                <p className="text-muted small mb-2" style={{ fontSize: '0.78rem' }}>
                                                    {clave === 'BIENVENIDA'
                                                        ? 'Este texto se insertará automáticamente al inicio de cada mensaje de bienvenida enviado por los asesores al tomar un ticket.'
                                                        : `Prefijo institucional obligatorio que se antepondrá a los mensajes de la categoría "${clave}".`}
                                                </p>

                                                <textarea
                                                    rows="3"
                                                    className="form-control mb-3"
                                                    value={currentPrefix}
                                                    onChange={e => handlePrefixChange(clave, e.target.value)}
                                                    placeholder="Ej. Hola. Gracias por comunicarte con nuestro equipo, en este momento te está atendiendo {{nombre_agente}}"
                                                    disabled={isSavingThis}
                                                    style={{
                                                        borderRadius: '8px',
                                                        backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                                        borderColor: isDark ? '#233142' : '#cbd5e1',
                                                        color: isDark ? '#f1f5f9' : '#1e293b',
                                                        fontSize: '0.88rem',
                                                        lineHeight: '1.6'
                                                    }}
                                                />

                                                {/* Variables Disponibles para el Prefijo */}
                                                <div className="mb-4">
                                                    <div className="small fw-semibold text-muted mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.04em' }}>
                                                        VARIABLES DISPONIBLES (CLIC PARA INSERTAR):
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {AVAILABLE_PREFIX_VARIABLES.map(v => (
                                                            <button
                                                                key={v.token}
                                                                type="button"
                                                                onClick={() => handleInsertVariable(clave, v.token)}
                                                                className="btn btn-sm rounded-pill px-2.5 py-1 border d-inline-flex align-items-center gap-1.5 shadow-none"
                                                                style={{
                                                                    fontSize: '0.78rem',
                                                                    backgroundColor: isDark ? '#1a2736' : '#f1f5f9',
                                                                    color: isDark ? '#93c5fd' : '#2563eb',
                                                                    borderColor: isDark ? '#2a3b4f' : '#cbd5e1'
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={v.icon} style={{ fontSize: '0.72rem' }} />
                                                                <span className="font-monospace fw-semibold">{v.token}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Vista Previa de cómo lo ve el Asesor */}
                                                <div
                                                    className="p-3 rounded-3 mb-4 border"
                                                    style={{
                                                        backgroundColor: isDark ? '#0d1520' : '#f8fafc',
                                                        borderColor: isDark ? '#1e2d3d' : '#e2e8f0'
                                                    }}
                                                >
                                                    <small className="text-muted fw-semibold d-block mb-2" style={{ fontSize: '0.74rem' }}>
                                                        VISTA PREVIA DEL BANNER EN EL MODAL DEL ASESOR:
                                                    </small>
                                                    <div
                                                        className="p-2.5 rounded-2 d-flex justify-content-between align-items-center flex-wrap gap-2 border"
                                                        style={{
                                                            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eff6ff',
                                                            borderColor: isDark ? 'rgba(59, 130, 246, 0.25)' : '#bfdbfe',
                                                            color: isDark ? '#93c5fd' : '#1e40af',
                                                            fontSize: '0.84rem'
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center gap-2">
                                                            <FontAwesomeIcon icon={faLock} />
                                                            <span>
                                                                <strong>[Prefijo fijo]:</strong>{' '}
                                                                {currentPrefix || <span className="opacity-50">Sin prefijo configurado</span>}
                                                            </span>
                                                        </div>
                                                        <span
                                                            className="badge d-inline-flex align-items-center gap-1"
                                                            style={{
                                                                backgroundColor: 'transparent',
                                                                color: isDark ? '#93c5fd' : '#475569',
                                                                fontSize: '0.74rem'
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faShieldAlt} /> No modificable
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Botones Cancelar y Guardar */}
                                                <div className="d-flex justify-content-end gap-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary rounded-pill px-3 shadow-none"
                                                        onClick={() => {
                                                            setPrefixMap(prev => ({ ...prev, [clave]: backupPrefix }));
                                                            setEditingKey(null);
                                                        }}
                                                        disabled={isSavingThis}
                                                        style={{ fontSize: '0.86rem' }}
                                                    >
                                                        Cancelar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary rounded-pill px-4 shadow-none d-flex align-items-center gap-2"
                                                        onClick={() => handleSavePrefix(clave)}
                                                        disabled={isSavingThis}
                                                        style={{ fontSize: '0.86rem' }}
                                                    >
                                                        {isSavingThis ? (
                                                            <>
                                                                <FontAwesomeIcon icon={faSpinner} spin />
                                                                <span>Guardando...</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FontAwesomeIcon icon={faSave} />
                                                                <span>Guardar Prefijo Institucional</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal para Crear Nueva Categoría */}
            {isCreateModalOpen && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', zIndex: 1060 }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div
                            className="modal-content border-0 shadow-lg"
                            style={{
                                backgroundColor: isDark ? '#121e2d' : '#ffffff',
                                borderRadius: '12px'
                            }}
                        >
                            <div
                                className="modal-header py-3 px-4 border-bottom d-flex align-items-center justify-content-between"
                                style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-circle"
                                        style={{
                                            width: '32px',
                                            height: '32px',
                                            backgroundColor: isDark ? 'rgba(37, 99, 235, 0.2)' : '#dbeafe',
                                            color: isDark ? '#60a5fa' : '#2563eb'
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faFolderPlus} style={{ fontSize: '0.9rem' }} />
                                    </div>
                                    <div>
                                        <h6 className="modal-title fw-bold mb-0" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                                            Nueva Categoría de Mensajes Automáticos
                                        </h6>
                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            Configuración de política institucional
                                        </small>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    disabled={isCreatingCategory}
                                    style={{ filter: isDark ? 'invert(1)' : 'none' }}
                                />
                            </div>

                            <div className="modal-body p-4">
                                {/* Clave de la Categoría */}
                                <div className="mb-3">
                                    <label
                                        className="form-label small fw-semibold mb-1"
                                        style={{ color: isDark ? '#cbd5e1' : '#475569' }}
                                    >
                                        Clave / Identificador de la Categoría: <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control font-monospace"
                                        placeholder="Ej. DESPEDIDA, ESPERA, SEGUIMIENTO"
                                        value={newCategoryKey}
                                        onChange={e => setNewCategoryKey(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                                        disabled={isCreatingCategory}
                                        style={{
                                            borderRadius: '8px',
                                            backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                            borderColor: isDark ? '#233142' : '#cbd5e1',
                                            color: isDark ? '#f1f5f9' : '#1e293b'
                                        }}
                                    />
                                    <small className="text-muted" style={{ fontSize: '0.74rem' }}>
                                        Identificador único en mayúsculas sin espacios.
                                    </small>
                                </div>

                                {/* Prefijo Institucional Obligatorio */}
                                <div className="mb-3">
                                    <label
                                        className="form-label small fw-semibold mb-1 d-flex align-items-center gap-1.5"
                                        style={{ color: isDark ? '#cbd5e1' : '#475569' }}
                                    >
                                        <FontAwesomeIcon icon={faLock} className="text-warning" style={{ fontSize: '0.8rem' }} />
                                        <span>Prefijo Institucional (Mensaje Base Fijo): <span className="text-danger">*</span></span>
                                    </label>
                                    <textarea
                                        rows="2"
                                        className="form-control mb-2"
                                        placeholder="Texto fijo predeterminado que no podrán alterar los asesores..."
                                        value={newCategoryPrefix}
                                        onChange={e => setNewCategoryPrefix(e.target.value)}
                                        disabled={isCreatingCategory}
                                        style={{
                                            borderRadius: '8px',
                                            backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                            borderColor: isDark ? '#233142' : '#cbd5e1',
                                            color: isDark ? '#f1f5f9' : '#1e293b',
                                            fontSize: '0.85rem'
                                        }}
                                    />
                                    {/* Botones de variables */}
                                    <div className="d-flex flex-wrap gap-1.5 mb-2">
                                        {AVAILABLE_PREFIX_VARIABLES.map(v => (
                                            <button
                                                key={v.token}
                                                type="button"
                                                onClick={() => setNewCategoryPrefix(prev => (prev ? `${prev} ${v.token}` : v.token))}
                                                className="btn btn-sm rounded-pill px-2 py-0.5 border shadow-none"
                                                style={{
                                                    fontSize: '0.74rem',
                                                    backgroundColor: isDark ? '#1a2736' : '#f1f5f9',
                                                    color: isDark ? '#93c5fd' : '#2563eb',
                                                    borderColor: isDark ? '#2a3b4f' : '#cbd5e1'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={v.icon} style={{ fontSize: '0.68rem' }} className="me-1" />
                                                <span className="font-monospace">{v.token}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <small className="text-muted" style={{ fontSize: '0.74rem' }}>
                                        Este texto es el mensaje base obligatorio de la categoría que no podrán alterar los asesores.
                                    </small>
                                </div>

                                {/* Mensaje Adicional Global Opcional */}
                                <div className="mb-2">
                                    <label
                                        className="form-label small fw-semibold mb-1"
                                        style={{ color: isDark ? '#cbd5e1' : '#475569' }}
                                    >
                                        Mensaje Adicional Global (Opcional):
                                    </label>
                                    <textarea
                                        rows="3"
                                        className="form-control"
                                        placeholder="Cuerpo de mensaje adicional complementario para esta categoría (opcional)..."
                                        value={newCategoryDefaultMessage}
                                        onChange={e => setNewCategoryDefaultMessage(e.target.value)}
                                        disabled={isCreatingCategory}
                                        style={{
                                            borderRadius: '8px',
                                            backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                            borderColor: isDark ? '#233142' : '#cbd5e1',
                                            color: isDark ? '#f1f5f9' : '#1e293b',
                                            fontSize: '0.85rem'
                                        }}
                                    />
                                    <small className="text-muted" style={{ fontSize: '0.74rem' }}>
                                        Texto adicional opcional. Si se deja vacío, la categoría enviará únicamente el prefijo institucional por defecto.
                                    </small>
                                </div>

                                {/* Alerta de Lenguaje Inapropiado */}
                                {((newCategoryPrefix.trim() && !validateInappropriateLanguage(newCategoryPrefix).isValid) ||
                                    (newCategoryDefaultMessage.trim() && !validateInappropriateLanguage(newCategoryDefaultMessage).isValid)) && (
                                        <div
                                            className="alert alert-danger d-flex align-items-center gap-2 p-2.5 mt-3 mb-0 rounded-2"
                                            style={{ fontSize: '0.82rem' }}
                                        >
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="flex-shrink-0" />
                                            <span>
                                                <strong>Lenguaje no permitido:</strong> Se detectaron palabras inapropiadas o prohibidas. Por favor, asegúrate de utilizar un lenguaje profesional.
                                            </span>
                                        </div>
                                    )}
                            </div>

                            <div
                                className="modal-footer py-2.5 px-4 border-top d-flex justify-content-end gap-2"
                                style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
                            >
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary rounded-pill px-3 shadow-none"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    disabled={isCreatingCategory}
                                    style={{ fontSize: '0.84rem' }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary rounded-pill px-4 shadow-none d-flex align-items-center gap-2 fw-semibold"
                                    onClick={handleCreateCategory}
                                    disabled={
                                        isCreatingCategory ||
                                        !newCategoryKey.trim() ||
                                        !newCategoryPrefix.trim() ||
                                        !validateInappropriateLanguage(newCategoryPrefix).isValid ||
                                        (newCategoryDefaultMessage.trim() && !validateInappropriateLanguage(newCategoryDefaultMessage).isValid)
                                    }
                                    style={{ fontSize: '0.84rem' }}
                                >
                                    {isCreatingCategory ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>Creando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faPlus} />
                                            <span>Crear Categoría</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GlobalMessagesSettingsSection;
